import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL = 15000;
// Session-scoped guard so the redirect fires at most once per browser session,
// even if the component remounts (e.g. due to React StrictMode double-render).
const SESSION_KEY = 'allure-role-change-notified';

/**
 * knownRole – the role we *know* this user currently has based on which app
 * is mounted. AdminBottomNav passes "admin"; StaffBottomNav passes "staff".
 * This prevents the loop where an already-admin user triggers the toast every
 * 15 s because `user.staff_role` is stale/missing in localStorage.
 */
export default function RoleChangeNotification({ knownRole = null }) {
  const currentRoleRef = useRef(null);
  const notifiedRef = useRef(false);

  useEffect(() => {
    // If already notified this session, skip entirely.
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Use the authoritative knownRole prop first; fall back to localStorage.
    if (knownRole) {
      currentRoleRef.current = knownRole;
    } else {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      currentRoleRef.current = user.staff_role || 'staff';
    }

    const poll = setInterval(async () => {
      if (notifiedRef.current) return;
      if (sessionStorage.getItem(SESSION_KEY)) { clearInterval(poll); return; }
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/venue/staff/my-role`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.data.found) return;
        const newRole = res.data.role;
        const oldRole = currentRoleRef.current;
        if (oldRole && newRole !== oldRole) {
          notifiedRef.current = true;
          clearInterval(poll);
          sessionStorage.setItem(SESSION_KEY, '1');
          // Update stored user object
          const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
          storedUser.staff_role = newRole;
          localStorage.setItem('user', JSON.stringify(storedUser));
          // Show a brief toast, then redirect
          const msg = newRole === 'admin'
            ? 'You have been promoted to admin.'
            : 'You are no longer an admin.';
          toast.success(msg, { duration: 2500, id: 'role-change' });
          setTimeout(() => {
            sessionStorage.removeItem(SESSION_KEY); // allow future real promotions
            if (newRole === 'admin') {
              window.location.href = '/staff-admin/people';
            } else {
              window.location.href = '/staff-app/people';
            }
          }, 2600);
        }
      } catch { /* silent */ }
    }, POLL_INTERVAL);

    return () => clearInterval(poll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
